import React, { useState } from 'react';
import { MusicTheory } from '../music-theory.js';

interface DegreeStats {
  attempts: number;
  correct: number;
  totalTime: number;
  bestTime: number | null;
  worstTime: number | null;
}

interface KeyStats {
  [degree: number]: DegreeStats;
}

interface StatsHeatmapProps {
  statsData: { [key: string]: KeyStats };
}

type ViewMode = 'accuracy' | 'time';

const StatsHeatmap: React.FC<StatsHeatmapProps> = ({ statsData }) => {
  const [viewMode, setViewMode] = useState<ViewMode>('accuracy');

  const keys = MusicTheory.KEYS;
  const degrees = [2, 3, 4, 5, 6, 7];

  const getAccuracyColor = (accuracy: number): string => {
    if (accuracy >= 0.9) return 'bg-emerald-500';
    if (accuracy >= 0.8) return 'bg-emerald-400';
    if (accuracy >= 0.7) return 'bg-yellow-400';
    if (accuracy >= 0.6) return 'bg-orange-400';
    if (accuracy >= 0.5) return 'bg-orange-500';
    return 'bg-red-500';
  };

  const getTimeColor = (avgTime: number): string => {
    // Green for < 3s, progressively more red as time increases
    if (avgTime <= 2000) return 'bg-emerald-500';
    if (avgTime <= 2500) return 'bg-emerald-400';
    if (avgTime <= 3000) return 'bg-yellow-400';
    if (avgTime <= 3500) return 'bg-orange-400';
    if (avgTime <= 4000) return 'bg-orange-500';
    return 'bg-red-500';
  };

  const getCellData = (key: string, degree: number) => {
    const keyData = statsData[key];
    if (!keyData || !keyData[degree]) {
      return { value: '-', color: 'bg-slate-100', textColor: 'text-slate-400' };
    }

    const degreeStats = keyData[degree];
    if (degreeStats.attempts === 0) {
      return { value: '-', color: 'bg-slate-100', textColor: 'text-slate-400' };
    }

    if (viewMode === 'accuracy') {
      const accuracy = degreeStats.correct / degreeStats.attempts;
      const percentage = Math.round(accuracy * 100);
      return {
        value: `${percentage}%`,
        color: getAccuracyColor(accuracy),
        textColor: 'text-white font-medium'
      };
    } else {
      const avgTime = degreeStats.totalTime / degreeStats.attempts;
      const seconds = (avgTime / 1000).toFixed(1);
      return {
        value: `${seconds}s`,
        color: getTimeColor(avgTime),
        textColor: 'text-white font-medium'
      };
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-0">
        <h3 className="text-sm uppercase tracking-wider text-slate-600 font-medium">
          Performance Heatmap
        </h3>

        <div className="w-fit flex gap-1 bg-slate-100 rounded-lg p-1">
          <button
            onClick={() => setViewMode('accuracy')}
            className={`
              px-3 py-1.5 rounded text-xs font-medium transition-all
              ${viewMode === 'accuracy' 
                ? 'bg-white text-slate-900 shadow-sm' 
                : 'text-slate-600 hover:text-slate-900'}
            `}
          >
            Accuracy
          </button>
          <button
            onClick={() => setViewMode('time')}
            className={`
              px-3 py-1.5 rounded text-xs font-medium transition-all
              ${viewMode === 'time' 
                ? 'bg-white text-slate-900 shadow-sm' 
                : 'text-slate-600 hover:text-slate-900'}
            `}
          >
            Response Time
          </button>
        </div>
      </div>

      <div className="overflow-x-auto -mx-2 sm:mx-0">
        <div className="inline-block min-w-full px-2 sm:px-0">
          <table className="w-full border-collapse table-fixed">
            <thead>
              <tr>
                <th className="p-1 sm:p-2 text-[0.625rem] sm:text-xs font-medium text-slate-500 text-left sticky left-0 bg-white z-10 w-10 sm:w-16">
                  Key
                </th>
                {degrees.map(degree => (
                  <th key={degree} className="p-1 sm:p-2 text-[0.625rem] sm:text-xs font-medium text-slate-500 text-center">
                    {degree}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {keys.map(key => (
                <tr key={key} className="border-t border-slate-100">
                  <td className="p-1 sm:p-2 text-xs sm:text-sm font-medium text-slate-700 sticky left-0 bg-white z-10 w-10 sm:w-16">
                    {key}
                  </td>
                  {degrees.map(degree => {
                    const cellData = getCellData(key, degree);
                    return (
                      <td key={degree} className="p-0.5 sm:p-1">
                        <div 
                          className={`
                            ${cellData.color} ${cellData.textColor}
                            rounded px-1 py-1.5 sm:px-2 sm:py-2.5 text-center text-[0.625rem] sm:text-xs
                            transition-all hover:scale-105 cursor-default
                            min-w-[2.5rem] sm:min-w-[3.5rem] w-full
                          `}
                          title={`${key} - Scale degree ${degree}`}
                        >
                          {cellData.value}
                        </div>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="flex items-center justify-between text-xs text-slate-500 pt-2">
        <div className="flex items-center gap-4">
          <span className="font-medium">Legend:</span>
          {viewMode === 'accuracy' ? (
            <div className="flex items-center gap-2">
              <div className="flex items-center gap-1">
                <div className="w-4 h-4 rounded bg-emerald-500"></div>
                <span>≥90%</span>
              </div>
              <div className="flex items-center gap-1">
                <div className="w-4 h-4 rounded bg-yellow-400"></div>
                <span>70-90%</span>
              </div>
              <div className="flex items-center gap-1">
                <div className="w-4 h-4 rounded bg-red-500"></div>
                <span>&lt;50%</span>
              </div>
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <div className="flex items-center gap-1">
                <div className="w-4 h-4 rounded bg-emerald-500"></div>
                <span>≤2.5s</span>
              </div>
              <div className="flex items-center gap-1">
                <div className="w-4 h-4 rounded bg-yellow-400"></div>
                <span>2.5-3s</span>
              </div>
              <div className="flex items-center gap-1">
                <div className="w-4 h-4 rounded bg-red-500"></div>
                <span>&gt;4s</span>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default StatsHeatmap;